import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

// ── Wer liegt vorn? ──────────────────────────────────────────────────────────────────────────────
//
// Sobald zwei Fenster offen sind (Cockpit, Details, Notizen), stellt sich die Frage, die jedes
// Fenster-System beantwortet: das zuletzt angetippte liegt oben. Vorher standen die Ebenen fest im
// Klassennamen — die Nachschlage-Fenster IMMER über dem Cockpit. Wer das Skript wiedersehen wollte,
// musste das Details-Fenster wegschieben oder schliessen, obwohl er es gleich wieder brauchte.
//
// Geführt als REIHENFOLGE, nicht als hochzählende Zahl: `z-index` wäre sonst nach ein paar hundert
// Klicks über den Dialogen und Meldungen der App gelandet (Bestätigungs-Dialog: 80). So bleiben die
// Ebenen immer zwischen 60 und 60+Anzahl — bei drei Fenstern also 60..62.

const BASIS = 60;

const stapel: symbol[] = [];
const hoerer = new Set<() => void>();

function melde() { for (const h of hoerer) h(); }

export function registriere(id: symbol): void {
  if (!stapel.includes(id)) stapel.push(id);
  melde();
}

export function entferne(id: symbol): void {
  const i = stapel.indexOf(id);
  if (i >= 0) stapel.splice(i, 1);
  melde();
}

/** Nach vorn holen. Liegt das Fenster schon oben, passiert nichts — sonst meldete jeder Klick eine
 *  Änderung und alle Fenster zeichneten neu. */
export function nachVornImStapel(id: symbol): void {
  const i = stapel.indexOf(id);
  if (i < 0 || i === stapel.length - 1) return;
  stapel.splice(i, 1);
  stapel.push(id);
  melde();
}

export function ebeneVon(id: symbol): number {
  const i = stapel.indexOf(id);
  return BASIS + (i < 0 ? 0 : i);
}

/** Nur für Tests: den Stapel leeren, damit Testfälle sich nicht gegenseitig sehen. */
export function _leere(): void {
  stapel.length = 0;
  melde();
}

/**
 * Die Ebene DIESES Fensters plus der Griff, es nach vorn zu holen. Beim Aufgehen liegt ein Fenster
 * automatisch oben — wer eines öffnet, will es sehen.
 */
export function useFensterEbene() {
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) idRef.current = Symbol('fenster');
  const id = idRef.current;

  const abonniere = useCallback((f: () => void) => {
    hoerer.add(f);
    return () => { hoerer.delete(f); };
  }, []);
  const ebene = useSyncExternalStore(abonniere, () => ebeneVon(id), () => BASIS);

  useEffect(() => {
    registriere(id);
    return () => entferne(id);
  }, [id]);

  const nachVorn = useCallback(() => nachVornImStapel(id), [id]);
  return { ebene, nachVorn };
}
